import { useEffect, useState } from "react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function DeposerAnnonce() {
  const [f, setF] = useState({
    profile: "particulier", name: "", email: "", phone: "", company: "", siret: "",
    city: "", postal_code: "", region: "", property_type: "libre", surface: 100, rooms: 4,
    market_value: 400000, bouquet: 100000, duration_years: 15, description: "",
    dpe_energy: "", dpe_climate: "", dpe_kwh: "", dpe_co2: "",
    has_notary: null, notary_name: "", notary_city: "",
    wants_partner_notary: null, partner_notary_id: "", partner_notary_name: "",
    certify_ownership: false,
    honeypot: "",
  });
  const [photos, setPhotos] = useState([]);
  const [partners, setPartners] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Fetch partner notaries when postal code or city changes and vendor has no notary
  useEffect(() => {
    if (f.has_notary !== false) { setPartners([]); return; }
    const params = new URLSearchParams();
    if (f.postal_code) params.set("postal_code", f.postal_code);
    api.get(`/partner-notaries?${params.toString()}`)
      .then(({ data }) => setPartners(Array.isArray(data) ? data : []))
      .catch(() => setPartners([]));
  }, [f.has_notary, f.postal_code]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const backendBase = api.defaults.baseURL.replace(/\/api$/, "");
      const image_urls = photos.map((p) => `${backendBase}/api/uploads/${p.id}`);
      await api.post("/submissions", { ...f, images: image_urls, image_file_ids: photos.map((p) => p.id) });
      setDone(true);
      toast.success("Annonce enregistrée. Elle sera publiée après modération.");
    } catch { toast.error("Erreur d'envoi, réessayez."); }
    finally { setBusy(false); }
  };

  return (
    <>
      <SEO title="Déposer une annonce — venteaterme.fr" description="Particuliers ou agences, déposez votre bien en vente à terme sur venteaterme.fr. Publication après validation par notre équipe." />
      <section className="pt-24 pb-12 bg-navy text-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-4">Marketplace</div>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight text-cream">Déposer une annonce</h1>
          <p className="text-cream/85 mt-6 max-w-2xl">Particuliers et agences immobilières&nbsp;: diffusez votre bien en vente à terme. Vos coordonnées ne sont jamais affichées publiquement — les prospects vous sont transmis via notre plateforme.</p>
          <p className="text-cream mt-4 max-w-2xl font-semibold">
            <span className="inline-block bg-accent-brand text-white text-xs uppercase tracking-widest font-bold px-3 py-1.5 mr-3">100&nbsp;% gratuit</span>
            Publication gratuite, sans commission, sans frais cachés.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          {done ? (
            <div className="bg-white border border-navy/10 p-10 text-center">
              <h2 className="font-serif text-3xl text-navy mb-4">Merci</h2>
              <p className="text-navy/70">Votre annonce a bien été enregistrée. Elle sera examinée par notre équipe et publiée dans les 48 h ouvrées.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="submission-form" className="bg-white border border-navy/10 p-8 md:p-10 space-y-5">
              <input type="text" name="website" tabIndex={-1} autoComplete="off"
                value={f.honeypot} onChange={(e) => setF({ ...f, honeypot: e.target.value })}
                style={{ position: "absolute", left: "-9999px" }} aria-hidden />

              <div>
                <Label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold">Vous êtes</Label>
                <RadioGroup value={f.profile} onValueChange={(v) => setF({ ...f, profile: v })} className="grid grid-cols-2 gap-3 mt-3">
                  {[["particulier", "Particulier"], ["agence", "Agence immobilière"]].map(([v, lb]) => (
                    <label key={v} data-testid={`sub-profile-${v}`}
                      className={`cursor-pointer border p-3 flex items-center gap-2 text-sm ${f.profile === v ? "border-accent-brand bg-accent-brand/5" : "border-navy/15"}`}>
                      <RadioGroupItem value={v} id={`sp-${v}`} /><span>{lb}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Nom du contact</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-2 border-navy/20" data-testid="sub-name" /></div>
                <div><Label>Email</Label><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-2 border-navy/20" data-testid="sub-email" /></div>
                <div><Label>Téléphone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-2 border-navy/20" /></div>
                {f.profile === "agence" && (
                  <>
                    <div><Label>Nom de l'agence</Label><Input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className="mt-2 border-navy/20" /></div>
                    <div><Label>SIRET</Label><Input value={f.siret} onChange={(e) => setF({ ...f, siret: e.target.value })} className="mt-2 border-navy/20" /></div>
                  </>
                )}
              </div>

              <div className="border-t border-navy/10 pt-5">
                <div className="text-xs uppercase tracking-wider text-accent-brand font-bold mb-3">Le bien</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Ville</Label><Input required value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="mt-2 border-navy/20" /></div>
                  <div><Label>Code postal</Label><Input data-testid="sub-postal" value={f.postal_code} onChange={(e) => setF({ ...f, postal_code: e.target.value })} className="mt-2 border-navy/20" placeholder="ex : 13003" /></div>
                  <div><Label>Région</Label><Input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} className="mt-2 border-navy/20" /></div>
                  <div><Label>Type</Label>
                    <Select value={f.property_type} onValueChange={(v) => setF({ ...f, property_type: v })}>
                      <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="libre">Libre</SelectItem><SelectItem value="occupee">Occupée</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Surface (m²)</Label><Input type="number" required value={f.surface} onChange={(e) => setF({ ...f, surface: +e.target.value })} className="mt-2 border-navy/20" /></div>
                  <div><Label>Pièces</Label><Input type="number" required value={f.rooms} onChange={(e) => setF({ ...f, rooms: +e.target.value })} className="mt-2 border-navy/20" /></div>
                  <div><Label>Valeur du bien (€)</Label><Input type="number" required value={f.market_value} onChange={(e) => setF({ ...f, market_value: +e.target.value })} className="mt-2 border-navy/20" /></div>
                  <div><Label>Bouquet souhaité (€)</Label><Input type="number" required value={f.bouquet} onChange={(e) => setF({ ...f, bouquet: +e.target.value })} className="mt-2 border-navy/20" /></div>
                  <div><Label>Durée (années)</Label><Input type="number" required min={5} max={30} value={f.duration_years} onChange={(e) => setF({ ...f, duration_years: +e.target.value })} className="mt-2 border-navy/20" /></div>
                </div>
              </div>

              <div className="border-t border-navy/10 pt-5">
                <div className="text-xs uppercase tracking-wider text-accent-brand font-bold mb-1">Diagnostic de Performance Énergétique (DPE)</div>
                <p className="text-xs text-navy/60 mb-3">La mention du DPE est obligatoire dans toute annonce immobilière (art. L.126-26 du CCH). Un logement classé F ou G est signalé « à consommation énergétique excessive ».</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Classe énergie (A→G)</Label>
                    <Select value={f.dpe_energy} onValueChange={(v) => setF({ ...f, dpe_energy: v })}>
                      <SelectTrigger data-testid="sub-dpe-energy" className="mt-2"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>{["A","B","C","D","E","F","G"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Classe climat (A→G)</Label>
                    <Select value={f.dpe_climate} onValueChange={(v) => setF({ ...f, dpe_climate: v })}>
                      <SelectTrigger data-testid="sub-dpe-climate" className="mt-2"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>{["A","B","C","D","E","F","G"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Consommation (kWh/m².an)</Label><Input type="number" value={f.dpe_kwh} onChange={(e) => setF({ ...f, dpe_kwh: e.target.value })} className="mt-2 border-navy/20" /></div>
                  <div><Label>Coûts annuels estimés (€/an)</Label><Input type="number" value={f.dpe_co2} onChange={(e) => setF({ ...f, dpe_co2: e.target.value })} className="mt-2 border-navy/20" placeholder="ex: 1200" /></div>
                </div>
                {(f.dpe_energy === "F" || f.dpe_energy === "G") && (
                  <div className="mt-3 p-3 bg-[#fdf2e9] border-l-4 border-[#c0392b] text-xs text-navy">
                    <strong>Attention&nbsp;: logement à consommation énergétique excessive.</strong> Depuis le 1er janvier 2025, les logements classés G ne peuvent plus être proposés à la location (interdiction progressive pour F en 2028 et E en 2034).
                  </div>
                )}
              </div>

              <div className="border-t border-navy/10 pt-5">
                <div className="text-xs uppercase tracking-wider text-accent-brand font-bold mb-1">Photos du bien</div>
                <p className="text-xs text-navy/60 mb-3">Ajoutez jusqu'à 10 photos. La première est utilisée comme visuel principal.</p>
                <ImageUploader value={photos} onChange={setPhotos} max={10} />
              </div>

              <div className="border-t border-navy/10 pt-5">
                <div className="text-xs uppercase tracking-wider text-accent-brand font-bold mb-1">Notaire du vendeur</div>
                <p className="text-xs text-navy/60 mb-3">Le vendeur a-t-il déjà un notaire pour cette opération ? (facultatif)</p>
                <RadioGroup
                  value={f.has_notary === true ? "oui" : f.has_notary === false ? "non" : ""}
                  onValueChange={(v) => setF({ ...f, has_notary: v === "oui" ? true : v === "non" ? false : null })}
                  className="grid grid-cols-2 gap-3"
                >
                  {[["oui", "Oui"], ["non", "Non / je ne sais pas"]].map(([v, lb]) => (
                    <label key={v} className={`cursor-pointer border p-3 flex items-center gap-2 text-sm ${(f.has_notary === (v === "oui")) ? "border-accent-brand bg-accent-brand/5" : "border-navy/15"}`}>
                      <RadioGroupItem value={v} id={`not-${v}`} data-testid={`sub-has-notary-${v}`} /><span>{lb}</span>
                    </label>
                  ))}
                </RadioGroup>
                {f.has_notary === true && (
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Nom / étude du notaire</Label>
                      <Input data-testid="sub-notary-name" value={f.notary_name} onChange={(e) => setF({ ...f, notary_name: e.target.value })} className="mt-2 border-navy/20" placeholder="ex : Étude Dupont & Associés" />
                    </div>
                    <div>
                      <Label>Ville de l'étude</Label>
                      <Input data-testid="sub-notary-city" value={f.notary_city} onChange={(e) => setF({ ...f, notary_city: e.target.value })} className="mt-2 border-navy/20" placeholder="ex : Marseille" />
                    </div>
                  </div>
                )}
                {f.has_notary === false && (
                  <div className="mt-5 border-t border-dashed border-navy/15 pt-4">
                    <div className="text-xs uppercase tracking-wider text-accent-brand font-bold mb-1">Notaire partenaire</div>
                    <p className="text-xs text-navy/60 mb-3">Souhaitez-vous être mis en relation avec un notaire partenaire ? (facultatif — mise en relation gratuite, sans engagement)</p>
                    <RadioGroup
                      value={f.wants_partner_notary === true ? "oui" : f.wants_partner_notary === false ? "non" : ""}
                      onValueChange={(v) => setF({ ...f, wants_partner_notary: v === "oui" ? true : v === "non" ? false : null, partner_notary_id: v === "oui" ? f.partner_notary_id : "", partner_notary_name: v === "oui" ? f.partner_notary_name : "" })}
                      className="grid grid-cols-2 gap-3"
                    >
                      {[["oui", "Oui, mettez-moi en relation"], ["non", "Non, merci"]].map(([v, lb]) => (
                        <label key={v} className={`cursor-pointer border p-3 flex items-center gap-2 text-sm ${(f.wants_partner_notary === (v === "oui")) ? "border-accent-brand bg-accent-brand/5" : "border-navy/15"}`}>
                          <RadioGroupItem value={v} id={`pn-${v}`} data-testid={`sub-wants-partner-${v}`} /><span>{lb}</span>
                        </label>
                      ))}
                    </RadioGroup>
                    {f.wants_partner_notary === true && (
                      <div className="mt-4 p-4 border-l-4 border-accent-brand bg-accent-brand/5" data-testid="partner-notary-generic">
                        <div className="text-sm text-navy font-semibold mb-1">Nous vous mettrons en relation avec un notaire partenaire de votre région.</div>
                        <p className="text-xs text-navy/70">Après validation de votre annonce, un notaire du département sera contacté pour prendre le relais. Cette mise en relation est gratuite et sans engagement.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label>Description du bien</Label>
                <Textarea required rows={5} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-2 border-navy/20" />
              </div>

              <div className="border-t border-navy/10 pt-5">
                <label className="flex items-start gap-3 cursor-pointer text-sm text-navy" data-testid="sub-certify-label">
                  <input
                    type="checkbox"
                    required
                    checked={f.certify_ownership}
                    onChange={(e) => setF({ ...f, certify_ownership: e.target.checked })}
                    data-testid="sub-certify-checkbox"
                    className="mt-1 w-4 h-4 accent-[#C9A227]"
                  />
                  <span>
                    <strong>Je certifie être le propriétaire du bien ou dûment mandaté.</strong>{" "}
                    <span className="text-xs text-navy/60 block mt-1">Aucun justificatif n'est requis au dépôt. Un document (taxe foncière, titre) pourra vous être demandé plus tard pour obtenir le badge <em>« Bien vérifié »</em>.</span>
                  </span>
                </label>
              </div>
              <button disabled={busy || !f.certify_ownership} data-testid="sub-submit" className="w-full bg-accent-brand text-white font-bold py-4 tracking-wide hover:bg-[#B08D22] transition-colors disabled:opacity-60">
                {busy ? "Envoi..." : "Envoyer pour modération"}
              </button>
              <p className="text-xs text-navy/55 border-t border-navy/10 pt-4">
                Les données transmises sont utilisées uniquement pour la modération et la publication de votre annonce. Vos coordonnées personnelles ne sont pas affichées publiquement. Voir notre <a href="/confidentialite" className="text-accent-brand underline">politique de confidentialité</a>.
              </p>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
